import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService, LoginRequest, LoginResponse } from '@/api/authService';
import { ES_ECOLAB, STORAGE } from '@/config/authConfig';
import ecolabAuthService from '@/api/ecolabAuthService';
import type { ContextoPOS, EmpresaDeUsuario } from '@/api/onboardingService';

// ── Tipos ─────────────────────────────────────────────────────

export interface UsuarioPOS {
  id: string;
  email: string;
  userName: string;
  nombre: string;
  apellido: string;
  nombreCompleto: string;
  imagen?: string;
  roles: string[];
  empresaId?: number;
  empresaNombre?: string;
  sucursalId?: number;
  sucursalNombre?: string;
  perfilPOS?: string;
  /** Rol del perfil en la empresa activa: Admin, Supervisor, Cajero, Mesero, Cocina… */
  rolBase?: string;
  usuarioPOSId?: number;
  /** Empresas de la persona (puede estar en varias). */
  empresasUsuario?: EmpresaDeUsuario[];
  descuentoMaximo: number;
  permiteDevolucion: boolean;
  permiteAnular: boolean;
  permiteModificarPrecio: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UsuarioPOS | null;
  token: string | null;
  error: string | null;
  /** Modo Ecolab: el asociado entró pero todavía no creó su empresa. */
  requiereOnboarding: boolean;
  /** Empresas del usuario (03_seguridad_por_perfil.sql permite varias). */
  empresas: number[];
}

// ── Helpers ───────────────────────────────────────────────────

const mapResponse = (r: LoginResponse): UsuarioPOS => ({
  id: r.email,
  email: r.email,
  userName: r.userName,
  nombre: r.nombre,
  apellido: r.apellido,
  nombreCompleto: `${r.nombre} ${r.apellido}`.trim(),
  imagen: r.imagen,
  roles: r.roles,
  empresaId: r.empresaId,
  empresaNombre: r.empresaNombre,
  sucursalId: r.sucursalId,
  sucursalNombre: r.sucursalNombre,
  perfilPOS: r.perfilPOS,
  descuentoMaximo: r.descuentoMaximo,
  permiteDevolucion: r.permiteDevolucion,
  permiteAnular: r.permiteAnular,
  permiteModificarPrecio: r.permiteModificarPrecio,
});

/**
 * Mapea la respuesta de /api/usuario/me en modo Ecolab al UsuarioPOS que
 * ya consume todo el frontend. Así ninguna vista existente tiene que
 * cambiar por el cambio de proveedor de identidad.
 */
const mapContexto = (c: ContextoPOS): UsuarioPOS => {
  // La empresa que resolvió el backend queda fija para los siguientes requests
  try {
    if (c.empresaActiva) localStorage.setItem(STORAGE.EMPRESA, String(c.empresaActiva));
    else localStorage.removeItem(STORAGE.EMPRESA);
  } catch { /* sin almacenamiento */ }
  const partes = (c.nombre || '').trim().split(' ');
  return {
    id: c.identidadId,
    email: c.email || '',
    userName: c.email || '',
    nombre: partes[0] || '',
    apellido: partes.slice(1).join(' '),
    nombreCompleto: c.nombre || c.email || '',
    roles: c.roles || [],
    empresaId: c.empresaActiva,
    empresaNombre: c.empresa?.nombreComercial || c.empresa?.razonSocial,
    sucursalId: c.sucursalPrincipal,
    sucursalNombre: c.sucursalNombre,
    perfilPOS: c.perfil?.nombre,
    rolBase: c.perfil?.rolBase,
    usuarioPOSId: c.usuarioPOSId,
    empresasUsuario: c.empresasDetalle ?? [],
    descuentoMaximo: c.permisos?.descuentoMaximo ?? 0,
    permiteDevolucion: c.permisos?.permiteDevolucion ?? false,
    permiteAnular: c.permisos?.permiteAnular ?? false,
    permiteModificarPrecio: c.permisos?.permiteModificarPrecio ?? false,
  };
};

// ── Thunks ────────────────────────────────────────────────────

export const loginAsync = createAsyncThunk<
  { token: string; user: UsuarioPOS; requiereOnboarding: boolean; empresas: number[] },
  LoginRequest
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    // ── Modo Ecolab ──────────────────────────────────────────
    // Las credenciales van a Ecolab, no al POS. El POS ya no valida
    // contraseñas: los usuarios viven en Ecolab.
    if (ES_ECOLAB) {
      try {
        if (!credentials.turnstileToken)
          return rejectWithValue('Espera a que se complete la verificación de seguridad.');
        const r = await ecolabAuthService.login(credentials.email, credentials.password, credentials.turnstileToken);

        localStorage.setItem(STORAGE.TOKEN, r.accessToken);
        if (r.refreshToken) localStorage.setItem(STORAGE.REFRESH, r.refreshToken);

        // Con el token de Ecolab, pedir el contexto POS.
        const ctx = await authService.getContexto();

        // NO se bloquea a quien no tiene empresa: se le manda al
        // onboarding para que la cree. Ese es el modelo del ecosistema.
        return {
          token: r.accessToken,
          user: mapContexto(ctx),
          requiereOnboarding: ctx.requiereOnboarding,
          empresas: ctx.empresas || [],
        };
      } catch (error: any) {
        localStorage.removeItem(STORAGE.TOKEN);
        localStorage.removeItem(STORAGE.REFRESH);
        return rejectWithValue(
          error?.message || 'Error al iniciar sesión. Verifique sus credenciales.'
        );
      }
    }

    // ── Modo Local ───────────────────────────────────────────
    try {
      const { turnstileToken: _t, ...credLocal } = credentials;
      const response = await authService.login(credLocal);

      // Bloquear acceso si no tiene empresa asignada en POS
      // (excepción: SuperAdmin y Admin pueden entrar sin empresa)
      const esSuperAdmin = response.roles?.includes('SuperAdmin');
      if (!esSuperAdmin && !response.empresaId) {
        return rejectWithValue(
          'Tu cuenta aún no tiene empresa asignada en el POS. Contacta al administrador para que configure tu acceso.'
        );
      }

      localStorage.setItem(STORAGE.TOKEN, response.token);
      return {
        token: response.token,
        user: mapResponse(response),
        requiereOnboarding: false,
        empresas: response.empresaId ? [response.empresaId] : [],
      };
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        'Error al iniciar sesión. Verifique sus credenciales.';
      return rejectWithValue(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  }
);

export const getMeAsync = createAsyncThunk<
  { token: string; user: UsuarioPOS; requiereOnboarding: boolean; empresas: number[] },
  void
>(
  'auth/getMe',
  async (_, { rejectWithValue }) => {
    // Modo Ecolab: /me NO devuelve token nuevo — el token lo emite Ecolab.
    // Se conserva el que ya está en localStorage.
    if (ES_ECOLAB) {
      try {
        const ctx = await authService.getContexto();
        const token = localStorage.getItem(STORAGE.TOKEN) || '';
        return {
          token,
          user: mapContexto(ctx),
          requiereOnboarding: ctx.requiereOnboarding,
          empresas: ctx.empresas || [],
        };
      } catch {
        localStorage.removeItem(STORAGE.TOKEN);
        localStorage.removeItem(STORAGE.REFRESH);
        return rejectWithValue('Sesión expirada');
      }
    }

    try {
      const response = await authService.getMe();
      localStorage.setItem(STORAGE.TOKEN, response.token);
      return {
        token: response.token,
        user: mapResponse(response),
        requiereOnboarding: false,
        empresas: response.empresaId ? [response.empresaId] : [],
      };
    } catch {
      localStorage.removeItem(STORAGE.TOKEN);
      return rejectWithValue('Sesión expirada');
    }
  }
);

// Alias para compatibilidad con axiosConfig
export const getCurrentUserAsync = getMeAsync;
export const refreshTokenAsync = getMeAsync;

// ── Slice ─────────────────────────────────────────────────────

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  token: localStorage.getItem(STORAGE.TOKEN),
  error: null,
  requiereOnboarding: false,
  empresas: [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
      state.requiereOnboarding = false;
      state.empresas = [];
      localStorage.removeItem(STORAGE.TOKEN);
      localStorage.removeItem(STORAGE.REFRESH);
      localStorage.removeItem(STORAGE.USER);
      localStorage.removeItem(STORAGE.EMPRESA);
    },
    clearError: (state) => { state.error = null; },
    refreshTokenAction: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem(STORAGE.TOKEN, action.payload);
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(loginAsync.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(loginAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.token = payload.token;
      state.user = payload.user;
      state.requiereOnboarding = payload.requiereOnboarding;
      state.empresas = payload.empresas;
      state.error = null;
    });
    builder.addCase(loginAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.error = payload as string;
    });

    // GetMe / RefreshToken
    builder.addCase(getMeAsync.pending, (state) => { state.isLoading = true; });
    builder.addCase(getMeAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.token = payload.token;
      state.user = payload.user;
      state.requiereOnboarding = payload.requiereOnboarding;
      state.empresas = payload.empresas;
    });
    builder.addCase(getMeAsync.rejected, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      localStorage.removeItem(STORAGE.TOKEN);
      localStorage.removeItem(STORAGE.REFRESH);
    });
  },
});

export const { logout, clearError, refreshTokenAction } = authSlice.actions;
export default authSlice.reducer;
